import React, { useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'

import { useAppSelector } from '../../../../store/hooks'
import { selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../../common/integrations'
import { toLocalISODate } from '../../../../common/dateUtils'

import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { DueDateInput } from './DueDateInput'
import { CrmRichTextEditor } from './CrmRichTextEditor'
import { useCrmAssignee } from './useCrmAssignee'

// `priority` maps to Redtail `importance`: "High"->3, "Normal"->2, else->1.
// The Redtail preset matches the literal "Normal" (NOT "Medium") for importance 2.
const PRIORITY_OPTIONS: SelectOption[] = [
  { id: 'Low', label: 'Low' },
  { id: 'Normal', label: 'Normal' },
  { id: 'High', label: 'High' },
]
const DEFAULT_PRIORITY_OPTION = PRIORITY_OPTIONS[1]

// Redtail activities use an activity code instead of a category. The preset
// defaults to 1 (= Task) when absent. This is a fixed list, not a fetched entity.
const ACTIVITY_CODE_OPTIONS: SelectOption[] = [
  { id: '1', label: 'Task' },
  { id: '2', label: 'Appointment' },
  { id: '3', label: 'Phone Call' },
  { id: '4', label: 'Unassigned' },
]
const DEFAULT_ACTIVITY_CODE_OPTION = ACTIVITY_CODE_OPTIONS[0]

export const RedtailTaskActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useCrmEligibility(IntegrationServiceId.REDTAIL, results ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.REDTAIL,
    sourceObject: 'task',
    entityType: 'task',
    relatedToType: 'Contact',
    successToast: { title: 'Creating task', message: 'Redtail task creation started.' },
    errorToast: { title: 'Failed to create task', message: 'Could not start the Redtail task creation. Please try again.' },
  })

  const [subject, setSubject] = useState('')
  const descriptionEditorRef = useRef<LexicalEditor>(null)
  const [descriptionText, setDescriptionText] = useState('')
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [selectedPriority, setSelectedPriority] = useState<SelectOption>(DEFAULT_PRIORITY_OPTION)
  const [selectedActivityCode, setSelectedActivityCode] = useState<SelectOption>(DEFAULT_ACTIVITY_CODE_OPTION)

  // `zocksAssignedTo` is the current user (the task creator); the picked teammate only
  // supplies the external Redtail user id used as the activity attendee.
  const clientOwner: string | undefined = eligibility.status === 'ready' && eligibility.eligibleCount === 1 ? eligibility.eligibleTargets[0].ownerId : undefined
  const { selectedAssignee, selectedAssigneeExternalId, assigneeOptions, selectedAssigneeOption, handleAssigneeOptionSelected } = useCrmAssignee(
    IntegrationServiceId.REDTAIL,
    clientOwner,
  )
  const currentUser = useAppSelector(selectAuthenticatedUser)

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'} in Redtail`
  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  // Name, assignee, and due date are required. Description is optional; priority and
  // activity code always hold a value, so they are inherently set.
  const isFormValid = subject.trim().length > 0 && dueDate !== null && selectedAssigneeExternalId !== null

  const handlePriorityOptionSelected = (option: SelectOption) => {
    setSelectedPriority(option)
  }

  const handleActivityCodeOptionSelected = (option: SelectOption) => {
    setSelectedActivityCode(option)
  }

  const handleSave = async () => {
    if (!isFormValid) {
      return
    }

    const zocksTask: Record<string, unknown> = {
      task: subject.trim(),
      priority: selectedPriority.id,
      // `status` is ignored by the Redtail preset (activities have no status field);
      // kept for parity with the Zocks SaveTaskDetails contract.
      status: 'open',
      activityCodeId: Number(selectedActivityCode.id),
    }
    const editor = descriptionEditorRef.current
    if (editor && descriptionText.trim().length > 0) {
      zocksTask.description = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    }
    if (dueDate) {
      // The Redtail preset requires the full ISO format (%Y-%m-%dT%H:%M:%S.%fZ) and only
      // uses the date. Keep the user's local calendar date and fix the time to midnight UTC.
      zocksTask.dueDate = `${toLocalISODate(dueDate)}T00:00:00.000Z`
    }
    if (selectedAssignee && selectedAssigneeExternalId) {
      // The picked teammate supplies the external Redtail USER id, which becomes the
      // activity `attendees[].user_id` (NOT a contact id).
      zocksTask.assignedTo = selectedAssigneeExternalId
    }
    // `zocksAssignedTo` is the current user, required by integrations-service SaveTaskDetails.
    if (currentUser?.id) {
      zocksTask.zocksAssignedTo = {
        type: 'internal',
        id: currentUser.id,
        user: {
          name: currentUser.name,
          type: 'internal',
        },
      }
    }

    await save({ tasks: [zocksTask] })
  }

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Redtail' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Redtail' />
    ) : (
      <div className='d-flex flex-column gap-3'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <Form.Group controlId='redtailTaskSubject' className='d-flex flex-column'>
          <Form.Label className='bold'>
            Activity name <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control value={subject} onChange={(e) => setSubject(e.target.value)} placeholder='Enter activity name' disabled={saving} />
        </Form.Group>
        <Form.Group controlId='redtailTaskDescription' className='d-flex flex-column'>
          <Form.Label className='bold'>Activity description</Form.Label>
          <CrmRichTextEditor
            ref={descriptionEditorRef}
            variant='task'
            onTextContentChange={setDescriptionText}
            placeholder={<>Enter activity description…</>}
          />
        </Form.Group>
        <Row className='g-3'>
          <Form.Group as={Col} controlId='redtailTaskAssignee' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Assignee <span className='text-danger'>*</span>
            </Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={assigneeOptions}
              initialSelectedOption={selectedAssigneeOption}
              onOptionSelected={handleAssigneeOptionSelected}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='redtailTaskDueDate' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Due date <span className='text-danger'>*</span>
            </Form.Label>
            <DatePicker
              selected={dueDate}
              onChange={(date: Date | null) => setDueDate(date)}
              todayButton='Today'
              dateFormat='dd/MM/yyyy'
              disabled={saving}
              customInput={<DueDateInput />}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='redtailTaskPriority' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>Priority</Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={PRIORITY_OPTIONS}
              initialSelectedOption={selectedPriority}
              onOptionSelected={handlePriorityOptionSelected}
            />
          </Form.Group>
          <Form.Group as={Col} controlId='redtailTaskActivityCode' xs={12} md={6} className='d-flex flex-column'>
            <Form.Label className='bold'>Activity type</Form.Label>
            <SelectDropdown
              className='py-2 zocks-px-0.75'
              options={ACTIVITY_CODE_OPTIONS}
              initialSelectedOption={selectedActivityCode}
              onOptionSelected={handleActivityCodeOptionSelected}
            />
          </Form.Group>
        </Row>
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='create an activity for' crmName='Redtail' />
      </div>
    )

  const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !isFormValid

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Create activity to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Create activity'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
